import * as THREE from "three/webgpu";
import {BehaviorSubject, type Observable, Subject} from 'rxjs';
import {
    isSketchCircleObject,
    isSketchLineObject,
    isSketchPointObject,
    SketchCircleObject,
    SketchCollectionCreationSource,
    SketchCollectionObject,
    SketchLineObject,
    type SketchPointObject,
} from '../mesh/sketch';
import {SketchDrawingHandler, SketchDrawingMouseDownPayload} from './SketchDrawingHandler';
import type {SketchDrawingManager} from "../manager/SketchDrawingManager";
import {SketchDrawingTool} from './DrawingSharedTypes';
import {addObject3DToParent} from '../utils/scene-object/addObject3DToParent';
import {
    isDashDotLIneNodeMaterial,
    ScreenSpaceDashedLineNodeMaterial,
    syncScreenSpaceDashedLineGeometry,
} from '../material';
import {convertMToMm} from '../utils/unit';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../sketch/config/SketchGeometryToleranceConfig';
import {SketchCircularDistributionCalculator} from '../sketch/circular-pattern/SketchCircularDistributionCalculator';
import {SketchCircularLinePatternBuilder} from '../sketch/circular-pattern/SketchCircularLinePatternBuilder';
import {SketchCircularPatternObject} from '../sketch/circular-pattern/SketchCircularPatternObject';
import type {SketchCircularLinePatternBuildResult} from '../../types/sketch/circular-pattern/SketchCircularLinePatternBuilder';
import type {SketchCircularPatternParameters} from '../../types/sketch/circular-pattern/SketchCircularPatternTypes';
import type {SketchPolygonRelationSyncPort} from '../../types/sketch/polygon/SketchPolygonRelationService';


/** 多边形预览颜色。 */
const POLYGON_PREVIEW_COLOR = 0x53a9ff;


/** 多边形预览屏幕空间虚线段长度。 */
const POLYGON_PREVIEW_DASH_SIZE = 7;
/** 多边形预览屏幕空间虚线间隔长度。 */
const POLYGON_PREVIEW_GAP_SIZE = 4;
/** 多边形预览虚线透明度。 */
const POLYGON_PREVIEW_DASHED_OPACITY = 0.8;
/** 多边形预览内部圆弧固定采样分段数。 */
const POLYGON_PREVIEW_ARC_SEGMENT_COUNT = 96;
/** 多边形右侧固定参考线长度相对内切圆半径的倍率。 */
const POLYGON_PREVIEW_RIGHT_LINE_INSCRIBED_RADIUS_MULTIPLIER = 1.5;


/**
 * 多边形绘制命令枚举。
 */
export enum SketchPolygonCommand {
    /** 多边形命令。 */
    Polygon = 'sketch-polygon',
}

/**
 * 多边形绘制模式枚举。
 */
export enum PolygonMode {
    /** 内接多边形。 */
    Inscribed = 'inscribed',
    /** 外切多边形。 */
    Circumscribed = 'circumscribed',
}


/**
 * 多边形绘制事件类型枚举。
 */
export enum PolygonDrawingEventType {
    /** 多边形一次绘制流程开始（已落下中心点）。 */
    Start = 'start',
    /** 多边形一次绘制流程结束（已创建实体或流程被重置）。 */
    End = 'end',
}


/**
 * 判断命令是否为多边形绘制命令。
 * @param command 命令字符串。
 * @returns 是否为多边形绘制命令。
 */
export function isSketchPolygonCommand(command: string): command is SketchPolygonCommand {
    return Object.values(SketchPolygonCommand).includes(command as SketchPolygonCommand);
}

/**
 * 多边形绘制选项。
 */
export interface PolygonOptions {
    /** 边数。 */
    sides: number;
    /** 多边形模式。 */
    mode: PolygonMode;
    /** 是否为构造几何。 */
    forConstruction: boolean;
}

/**
 * 多边形绘制参数。
 */
export interface PolygonParameters {
    /** 中心点。 */
    center?: [number, number, number];
    /** 中心点2D坐标。 */
    center2d?: [number, number];
    /** 半径。 */
    radius?: number;
    /** 旋转角。 */
    rotation?: number;
    /** 边数。 */
    sides?: number;
    /** 模式。 */
    mode?: PolygonMode;
}

/**
 * 草图多边形预览处理器。
 */
export class SketchPolygonDrawingHandler extends SketchDrawingHandler {
    /** 正多边形顶点圆周分布计算器。 */
    private readonly circularDistributionCalculator = new SketchCircularDistributionCalculator();

    /** 多边形种子线相切关系与阵列实例关系同步端口。 */
    private readonly polygonRelationSyncPort: SketchPolygonRelationSyncPort;

    /** 从唯一种子线构建圆周直线阵列的领域服务。 */
    private readonly circularLinePatternBuilder: SketchCircularLinePatternBuilder;

    /** 当前处理器负责的工具类型。 */
    readonly tool: SketchDrawingTool = SketchDrawingTool.Polygon;

    /** 多边形工具选项。 */
    public polygonOptions: PolygonOptions = {
        sides: 6,
        mode: PolygonMode.Inscribed,
        forConstruction: false,
    };

    /** 多边形参数。 */
    public polygonParameters: PolygonParameters | null = null;
    /** 多边形参数变化流。 */
    private readonly polygonParametersSubject: BehaviorSubject<PolygonParameters | null> = new BehaviorSubject<PolygonParameters | null>(null);

    /** 多边形绘制事件流。 */
    private readonly polygonDrawingEventSubject: Subject<PolygonDrawingEventType> = new Subject<PolygonDrawingEventType>();

    /** 最后一次预览时的鼠标位置，用于选项变更后立即刷新预览。 */
    private lastPreviewPoint: THREE.Vector3 | null = null;

    /** 当前绘制流程最近一次生成的多边形线集合对象。 */
    private currentPolygonCollectionObject: SketchCollectionObject | null = null;


    /** 多边形外轮廓预览对象缓存。 */
    private polygonPreviewRect: THREE.Line | null = null;
    /** 多边形内部圆弧预览对象缓存。 */
    private polygonPreviewArc: THREE.Line | null = null;
    /** 多边形右侧固定参考线预览对象缓存。 */
    private polygonFixedReferenceLinePreview: THREE.Line | null = null;
    /** 多边形当前指针参考线预览对象缓存。 */
    private polygonPointerReferenceLinePreview: THREE.Line | null = null;
    /** 多边形预览外轮廓当前是否为构造线样式。 */
    private polygonPreviewIsConstruction: boolean | null = null;
    /** 多边形预览中心局部坐标复用对象。 */
    private readonly polygonPreviewLocalCenter = new THREE.Vector2();
    /** 多边形预览当前指针局部坐标复用对象。 */
    private readonly polygonPreviewLocalCurrent = new THREE.Vector2();
    /** 多边形预览右侧参考点局部坐标复用对象。 */
    private readonly polygonPreviewLocalRight = new THREE.Vector2();
    /** 多边形外轮廓预览采样点复用数组。 */
    private readonly polygonPreviewOutlinePoints: THREE.Vector3[] = [];
    /** 多边形内部圆弧预览采样点复用数组。 */
    private readonly polygonPreviewArcPoints = Array.from(
        {length: POLYGON_PREVIEW_ARC_SEGMENT_COUNT + 1},
        () => new THREE.Vector3(),
    );
    /** 多边形右侧固定参考线预览采样点复用数组。 */
    private readonly polygonFixedReferenceLinePoints = [
        new THREE.Vector3(),
        new THREE.Vector3(),
    ];
    /** 多边形当前指针参考线预览采样点复用数组。 */
    private readonly polygonPointerReferenceLinePoints = [
        new THREE.Vector3(),
        new THREE.Vector3(),
    ];

    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     * @param polygonRelationSyncPort 多边形种子线相切关系与阵列实例关系同步端口。
     * @param circularLinePatternBuilder 圆周直线阵列构建器。
     */
    constructor(
        sketchDrawingManager: SketchDrawingManager,
        polygonRelationSyncPort: SketchPolygonRelationSyncPort,
        circularLinePatternBuilder: SketchCircularLinePatternBuilder,
    ) {
        super(sketchDrawingManager);
        this.polygonRelationSyncPort = polygonRelationSyncPort;
        this.circularLinePatternBuilder = circularLinePatternBuilder;
    }

    
    getRequiredPointCount(): number {
        return 2;
    }

    
    get closable(): boolean {
        return false;
    }

    
    resetState(): void {
        this.drawingPoints = [];
        this.polygonParameters = null;
        this.polygonParametersSubject.next(null);
        this.lastPreviewPoint = null;

        this.currentPolygonCollectionObject = null;


        this.clearPolygonPreviewCache();


        this.dispatchPolygonDrawingEvent(PolygonDrawingEventType.End);

    }

    /**
     * 监听多边形绘制参数变化。
     * @returns 多边形绘制参数变化流。
     */
    public observePolygonParameters(): Observable<PolygonParameters | null> {
        return this.polygonParametersSubject.asObservable();
    }


    /**
     * 监听多边形绘制开始/结束事件。
     * @returns 多边形绘制事件流。
     */
    public observePolygonDrawingEvent(): Observable<PolygonDrawingEventType> {
        return this.polygonDrawingEventSubject.asObservable();
    }

    /**
     * 派发多边形绘制事件。
     * @param eventType 绘制事件类型。
     */
    private dispatchPolygonDrawingEvent(eventType: PolygonDrawingEventType): void {
        this.polygonDrawingEventSubject.next(eventType);
    }


    /**
     * 处理多边形绘制鼠标按下事件。
     * @param payload 鼠标按下载荷。
     */
    handleMouseDown(payload: SketchDrawingMouseDownPayload): void {
        const resolvedPoint = payload.snappedPoint ?? this.getDrawingPoint(payload.event);
        this.drawingPoints.push(resolvedPoint.clone());

        if (this.drawingPoints.length === 1) {
            this.dispatchPolygonDrawingEvent(PolygonDrawingEventType.Start);
        }

        if (this.drawingPoints.length !== 2) {
            return;
        }
        this.createPolygonEntityByHandler();

        this.cleanupDrawingState();

        this.dispatchPolygonDrawingEvent(PolygonDrawingEventType.End);


    }


    /**
     * 获取多边形工具绘制提示。
     * @returns 提示文本。
     */
    getDrawingHint(): string {
        const edgeHint = this.polygonOptions.mode === PolygonMode.Inscribed
            ? '点击内切圆上的点'
            : '点击外接圆上的点';
        const baseHint = this.drawingPoints.length === 0
            ? '步骤 1/2：点击中心点'
            : `步骤 2/2：${edgeHint}`;
        return `${baseHint} | ESC取消`;
    }


    /**
     * 更新多边形预览。
     * @param currentPoint 当前预览点。
     */
    updatePolygonPreviewByHandler(currentPoint: THREE.Vector3): void {
        // 记录最后一次预览点，供选项变更时立即刷新预览使用
        this.lastPreviewPoint = currentPoint.clone();
        this.createPolygonPreviewInHandler(currentPoint);
    }

    /**
     * 统一预览更新入口。
     * @param currentPoint 当前预览点。
     */
    updatePreview(currentPoint: THREE.Vector3): void {
        this.updatePolygonPreviewByHandler(currentPoint);
    }

    /**
     * 使用最后记录的预览点刷新当前预览。
     * 当选项（边数、模式等）在面板中变更时调用，无需等待下次鼠标移动。
     */
    public refreshPreview(): void {
        // 仅在已放置中心点且有最后预览点时才刷新
        if (this.drawingPoints.length === 1 && this.lastPreviewPoint) {
            this.createPolygonPreviewInHandler(this.lastPreviewPoint);
        }
    }

    /**
     * 根据参数同步当前多边形绘制预览。
     * 说明：
     * 1. 仅在当前处于“已放置中心点、等待第二点”的绘制态下生效；
     * 2. 参数中的半径和旋转角按当前多边形模式反推边点，再复用既有预览更新链；
     * 3. 同步成功时会更新本地参数快照并立即刷新预览。
     * @param parameters 多边形参数。
     * @returns 是否同步成功。
     */
    public syncPolygonDrawingPreviewByParameters(parameters: PolygonParameters): boolean {
        if (this.drawingPoints.length !== 1 || !parameters.center2d || !parameters.radius) {
            return false;
        }

        const centerPoint = this.referencePlane.localToWorld(
            new THREE.Vector2(parameters.center2d[0], parameters.center2d[1]),
        );
        this.drawingPoints[0] = centerPoint.clone();

        const sides = Math.max(3, Math.floor(this.polygonOptions.sides || parameters.sides || 3));
        const step = (Math.PI * 2) / sides;
        const rotationRad = THREE.MathUtils.degToRad(parameters.rotation ?? 0);
        const edgeAngle = this.polygonOptions.mode === PolygonMode.Inscribed
            ? rotationRad - step / 2
            : rotationRad;
        const edgeLocal = new THREE.Vector2(
            parameters.center2d[0] + Math.cos(edgeAngle) * parameters.radius,
            parameters.center2d[1] + Math.sin(edgeAngle) * parameters.radius,
        );
        const edgePoint = this.referencePlane.localToWorld(edgeLocal);

        this.updatePolygonPreviewByHandler(edgePoint);
        return true;
    }


    /**
     * 获取当前绘制流程最近一次生成的多边形线集合对象。
     * @returns 多边形线集合对象；不存在时返回 null。
     */
    public getCurrentPolygonCollectionObject(): SketchCollectionObject | null {
        return this.currentPolygonCollectionObject;
    }


    /**
     * 根据中心点和边点构建正多边形顶点。
     * @param center 中心世界坐标。
     * @param edgePoint 边点世界坐标。
     * @param options 多边形选项。
     * @returns 多边形数据，若无效返回null。
     */
    public buildRegularPolygonFromCenter(
        center: THREE.Vector3,
        edgePoint: THREE.Vector3,
        options: PolygonOptions,
    ): {
        points: THREE.Vector3[];
        localCenter: THREE.Vector2;
        referenceRadius: number;
        circumRadius: number;
        rotation: number;
    } | null {
        const sides = Math.max(3, Math.floor(options.sides || 3));
        const localCenterRaw = this.referencePlane.worldToLocal(center);
        const localCenter = new THREE.Vector2(localCenterRaw.x, localCenterRaw.y);
        const localEdge = this.referencePlane.worldToLocal(edgePoint);
        const dx = localEdge.x - localCenter.x;
        const dy = localEdge.y - localCenter.y;
        const referenceRadius = Math.hypot(dx, dy);
        if (referenceRadius < SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return null;
        }
        const step = (Math.PI * 2) / sides;
        let rotation = Math.atan2(dy, dx);
        let circumRadius = referenceRadius;
        if (options.mode === PolygonMode.Inscribed) {
            const cosHalf = Math.cos(step / 2);
            if (Math.abs(cosHalf) < SKETCH_GEOMETRY_TOLERANCE_CONFIG.trigonometricDenominator) {
                return null;
            }
            circumRadius = referenceRadius / cosHalf;
            rotation += step / 2;
        }
        const points = this.circularDistributionCalculator.calculate({
            instanceCount: sides,
            startAngleRad: rotation,
            totalAngleRad: Math.PI * 2,
            equalSpacing: true,
            direction: 'counterclockwise',
        }).map((item) => {
            const angle = item.angleRad;
            const localX = localCenter.x + circumRadius * Math.cos(angle);
            const localY = localCenter.y + circumRadius * Math.sin(angle);
            return this.referencePlane.localToWorld(new THREE.Vector2(localX, localY));
        });
        return {
            points,
            localCenter: localCenter.clone(),
            referenceRadius,
            circumRadius,
            rotation,
        };
    }


    /**
     * 解析当前模式下需要创建的辅助圆半径。
     * 说明：
     * 1. 外接圆模式：使用外接圆半径；
     * 2. 内切圆模式：使用内切圆半径（即参考半径）。
     * @param referenceRadius 参考半径（用户输入/预览半径）。
     * @param circumRadius 外接圆半径。
     * @returns 当前模式下辅助圆半径。
     */
    private resolveConstructionCircleRadius(referenceRadius: number, circumRadius: number): number {
        if (this.polygonOptions.mode === PolygonMode.Circumscribed) {
            return circumRadius;
        }
        if (this.polygonOptions.mode === PolygonMode.Inscribed) {
            return referenceRadius;
        }
        throw new Error(`未知多边形模式，无法解析辅助圆半径: ${String(this.polygonOptions.mode)}`);
    }

    /**
     * 解析当前多边形的内切圆半径。
     * @param referenceRadius 参考半径。
     * @param circumRadius 外接圆半径。
     * @returns 内切圆半径。
     */
    private resolveInscribedCircleRadius(referenceRadius: number, circumRadius: number): number {
        if (this.polygonOptions.mode === PolygonMode.Inscribed) {
            return referenceRadius;
        }

        const sides = Math.max(3, Math.floor(this.polygonOptions.sides || 3));
        const halfStepCos = Math.cos(Math.PI / sides);
        if (Math.abs(halfStepCos) < SKETCH_GEOMETRY_TOLERANCE_CONFIG.trigonometricDenominator) {
            throw new Error('多边形内切圆半径计算失败：余弦值无效。');
        }

        return circumRadius * halfStepCos;
    }


    /**
     * 创建多边形预览屏幕空间虚线材质。
     * 说明：
     * 1. 预览圆弧与两条参考线属于绘制过程辅助对象，不是最终草图实体；
     * 2. 使用材质目录下的屏幕空间虚线材质，避免草图缩放后虚线节奏被底层米制长度拉伸；
     * 3. 每个预览对象独享材质实例，因为材质内部会记录当前折线点列。
     * @param points 当前折线点列。
     * @param materialName 材质调试名称。
     * @returns 多边形预览虚线材质。
     */
    private createPolygonDashedPreviewMaterial(
        points: THREE.Vector3[],
        materialName: string,
    ): ScreenSpaceDashedLineNodeMaterial {
        // 将统一的多边形预览视觉参数集中在这里，避免各辅助图元重复配置虚线材质。
        const material = new ScreenSpaceDashedLineNodeMaterial({
            color: POLYGON_PREVIEW_COLOR,
            linewidth: 1,
            dashSize: POLYGON_PREVIEW_DASH_SIZE,
            gapSize: POLYGON_PREVIEW_GAP_SIZE,
            opacity: POLYGON_PREVIEW_DASHED_OPACITY,
            transparent: true,
            points,
        });
        Reflect.set(material, 'name', materialName);
        return material;
    }

    /**
     * 创建多边形预览虚线对象。
     * @param points 当前折线点列。
     * @param materialName 材质调试名称。
     * @returns 多边形预览虚线对象。
     */
    private createPolygonDashedPreviewLine(
        points: THREE.Vector3[],
        materialName: string,
    ): THREE.Line {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const previewLine = new THREE.Line(
            geometry,
            this.createPolygonDashedPreviewMaterial(points, materialName),
        );

        // Node 虚线材质依赖 lineDistancePercent attribute，创建后必须同步一次几何距离。
        syncScreenSpaceDashedLineGeometry(previewLine);
        return previewLine;
    }


    /**
     * 销毁当前绘制预览并清空多边形预览缓存引用。
     * 说明：多边形预览对象会被高频复用，任何外部清理都必须同步让缓存失效。
     */
    protected override destroyPreview(): void {
        super.destroyPreview();
        this.clearPolygonPreviewCache();
    }

    /**
     * 清空多边形预览缓存引用。
     */
    private clearPolygonPreviewCache(): void {
        this.polygonPreviewRect = null;
        this.polygonPreviewArc = null;
        this.polygonFixedReferenceLinePreview = null;
        this.polygonPointerReferenceLinePreview = null;
        this.polygonPreviewIsConstruction = null;
    }

    /**
     * 判断当前多边形预览缓存是否仍可复用。
     * @param isConstruction 本次外轮廓是否为构造线样式。
     * @returns 是否可复用当前预览对象。
     */
    private isPolygonPreviewActive(isConstruction: boolean): boolean {
        const isOutlineActive = Boolean(
            this.polygonPreviewRect
            && this.sketchDrawingManager.previewRect === this.polygonPreviewRect
            && this.polygonPreviewIsConstruction === isConstruction,
        );
        const isArcActive = Boolean(
            this.polygonPreviewArc
            && this.sketchDrawingManager.previewCircle === this.polygonPreviewArc,
        );
        const isReferenceLineActive = Boolean(
            this.polygonFixedReferenceLinePreview
            && this.polygonPointerReferenceLinePreview
            && this.sketchDrawingManager.previewConstructionLines[0] === this.polygonFixedReferenceLinePreview
            && this.sketchDrawingManager.previewConstructionLines[1] === this.polygonPointerReferenceLinePreview,
        );

        return isOutlineActive && isArcActive && isReferenceLineActive;
    }

    /**
     * 确保多边形外轮廓复用点数组长度正确。
     * @param pointCount 需要的点数量。
     */
    private ensurePolygonPreviewOutlinePointCount(pointCount: number): void {
        while (this.polygonPreviewOutlinePoints.length < pointCount) {
            this.polygonPreviewOutlinePoints.push(new THREE.Vector3());
        }
        this.polygonPreviewOutlinePoints.length = pointCount;
    }

    /**
     * 原地更新预览线几何。
     * @param line 待更新的预览线。
     * @param points 新点列。
     */
    private updatePolygonPreviewLineGeometry(line: THREE.Line, points: THREE.Vector3[]): void {
        const positionAttribute = line.geometry.getAttribute('position');
        if (positionAttribute instanceof THREE.BufferAttribute && positionAttribute.count === points.length) {
            points.forEach((point, index) => {
                positionAttribute.setXYZ(index, point.x, point.y, point.z);
            });
            positionAttribute.needsUpdate = true;
        } else {
            line.geometry.setFromPoints(points);
        }

        line.geometry.computeBoundingSphere();
    }

    /**
     * 原地更新屏幕空间虚线预览线几何。
     * @param line 待更新的虚线预览线。
     * @param points 新点列。
     */
    private updatePolygonDashedPreviewLine(line: THREE.Line, points: THREE.Vector3[]): void {
        this.updatePolygonPreviewLineGeometry(line, points);
        syncScreenSpaceDashedLineGeometry(line);
    }

    /**
     * 原地更新多边形外轮廓闭合点列。
     * @param polygonPoints 多边形顶点。
     * @returns 可写入预览几何的闭合点列。
     */
    private updatePolygonPreviewOutlinePoints(polygonPoints: THREE.Vector3[]): THREE.Vector3[] {
        this.ensurePolygonPreviewOutlinePointCount(polygonPoints.length + 1);

        polygonPoints.forEach((point, index) => {
            this.polygonPreviewOutlinePoints[index].copy(point);
        });
        this.polygonPreviewOutlinePoints[polygonPoints.length].copy(polygonPoints[0]);

        return this.polygonPreviewOutlinePoints;
    }

    /**
     * 创建多边形外轮廓预览对象。
     * @param points 闭合外轮廓点列。
     * @param isConstruction 是否构造线。
     * @returns 多边形外轮廓预览对象。
     */
    private createPolygonPreviewOutlineLine(points: THREE.Vector3[], isConstruction: boolean): THREE.Line {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = isConstruction
            ? new THREE.LineDashedMaterial({
                color: POLYGON_PREVIEW_COLOR,
                linewidth: 1,
                dashSize: 3,
                gapSize: 2,
                opacity: 0.8,
                transparent: true,
            })
            : new THREE.LineBasicMaterial({
                color: POLYGON_PREVIEW_COLOR,
                linewidth: 2,
                opacity: 0.8,
                transparent: true,
            });
        const previewLine = new THREE.Line(geometry, material);
        if (material instanceof THREE.LineDashedMaterial) {
            previewLine.computeLineDistances();
        }

        return previewLine;
    }

    /**
     * 更新多边形外轮廓预览对象。
     * @param points 闭合外轮廓点列。
     * @param isConstruction 是否构造线。
     */
    private updatePolygonPreviewOutlineLine(points: THREE.Vector3[], isConstruction: boolean): void {
        if (!this.polygonPreviewRect || this.sketchDrawingManager.previewRect !== this.polygonPreviewRect) {
            this.sketchDrawingManager.previewRect = this.createPolygonPreviewOutlineLine(points, isConstruction);
            this.polygonPreviewRect = this.sketchDrawingManager.previewRect;
            this.polygonPreviewIsConstruction = isConstruction;
            this.viewer.addObject(this.sketchDrawingManager.previewRect);
            return;
        }

        this.updatePolygonPreviewLineGeometry(this.polygonPreviewRect, points);
        if (isConstruction) {
            this.polygonPreviewRect.computeLineDistances();
        }
    }

    /**
     * 原地更新多边形内部圆弧预览点列。
     * @param center 圆心。
     * @param radius 圆弧半径。
     * @param startAngle 起始角。
     * @param sweepAngle 扫掠角。
     * @returns 可写入预览几何的圆弧点列。
     */
    private updatePolygonPreviewArcPoints(
        center: THREE.Vector3,
        radius: number,
        startAngle: number,
        sweepAngle: number,
    ): THREE.Vector3[] {
        const localCenter = this.referencePlane.worldToLocalTo(center, this.polygonPreviewLocalCenter);

        for (let index = 0; index <= POLYGON_PREVIEW_ARC_SEGMENT_COUNT; index += 1) {
            const progress = index / POLYGON_PREVIEW_ARC_SEGMENT_COUNT;
            const angle = startAngle + sweepAngle * progress;
            const localX = localCenter.x + radius * Math.cos(angle);
            const localY = localCenter.y + radius * Math.sin(angle);
            // 固定采样数量并复用 Vector3，避免鼠标移动时因圆弧角度变化反复重建几何。
            this.referencePlane.localToWorldTo(localX, localY, this.polygonPreviewArcPoints[index]);
        }

        return this.polygonPreviewArcPoints;
    }

    /**
     * 原地更新多边形右侧固定参考线点列。
     * @param center 圆心。
     * @param rightPoint 右侧参考点。
     * @returns 可写入预览几何的参考线点列。
     */
    private updatePolygonFixedReferenceLinePoints(
        center: THREE.Vector3,
        rightPoint: THREE.Vector3,
    ): THREE.Vector3[] {
        this.polygonFixedReferenceLinePoints[0].copy(center);
        this.polygonFixedReferenceLinePoints[1].copy(rightPoint);
        return this.polygonFixedReferenceLinePoints;
    }

    /**
     * 原地更新多边形当前指针参考线点列。
     * @param center 圆心。
     * @param currentPoint 当前指针点。
     * @returns 可写入预览几何的参考线点列。
     */
    private updatePolygonPointerReferenceLinePoints(
        center: THREE.Vector3,
        currentPoint: THREE.Vector3,
    ): THREE.Vector3[] {
        this.polygonPointerReferenceLinePoints[0].copy(center);
        this.polygonPointerReferenceLinePoints[1].copy(currentPoint);
        return this.polygonPointerReferenceLinePoints;
    }


    /**
     * 创建多边形预览辅助线。
     * @param points 参考线点列。
     * @param materialName 材质调试名称。
     * @returns 预览辅助线对象。
     */
    private createPolygonPreviewConstructionLine(points: THREE.Vector3[], materialName: string): THREE.Line {
        return this.createPolygonDashedPreviewLine(
            points,
            materialName,
        );
    }

    /**
     * 创建多边形预览内部圆弧。
     * @param points 圆弧点列。
     * @returns 预览圆弧对象。
     */
    private createPolygonPreviewArc(points: THREE.Vector3[]): THREE.Line {
        return this.createPolygonDashedPreviewLine(
            points,
            '多边形预览圆弧-屏幕空间虚线材质',
        );
    }

    /**
     * 解析多边形预览右侧水平参考线终点。
     * 说明：
     * 1. 该参考线不固定吸附在多边形顶点或边上；
     * 2. 参考线需始终保持草图局部坐标系中的水平向右；
     * 3. 终点长度固定为内切圆半径的 1.5 倍，避免随顶点朝向产生跳变。
     * @param center 多边形中心。
     * @param inscribedRadius 内切圆半径。
     * @param out 输出右侧水平参考线终点。
     * @returns 输出右侧水平参考线终点。
     */
    private resolvePolygonPreviewHorizontalRightPoint(
        center: THREE.Vector3,
        inscribedRadius: number,
        out: THREE.Vector3,
    ): THREE.Vector3 {
        const centerLocal = this.referencePlane.worldToLocalTo(center, this.polygonPreviewLocalCenter);
        this.polygonPreviewLocalRight.set(
            centerLocal.x + inscribedRadius * POLYGON_PREVIEW_RIGHT_LINE_INSCRIBED_RADIUS_MULTIPLIER,
            centerLocal.y,
        );

        return this.referencePlane.localToWorldTo(this.polygonPreviewLocalRight, out);
    }

    /**
     * 同步多边形内部圆弧与参考线预览。
     * @param center 多边形中心。
     * @param currentPoint 当前鼠标点。
     * @param referenceRadius 参考半径。
     * @param circumRadius 外接圆半径。
     */
    private syncPolygonInternalArcPreview(
        center: THREE.Vector3,
        currentPoint: THREE.Vector3,
        referenceRadius: number,
        circumRadius: number,
    ): void {
        const inscribedRadius = this.resolveInscribedCircleRadius(referenceRadius, circumRadius);
        const previewArcRadius = inscribedRadius / 2;
        if (!Number.isFinite(previewArcRadius) || previewArcRadius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            this.sketchDrawingManager.hidePreviewAngleLabel();
            return;
        }

        const horizontalRightPoint = this.resolvePolygonPreviewHorizontalRightPoint(
            center,
            inscribedRadius,
            this.polygonFixedReferenceLinePoints[1],
        );

        const centerLocal = this.referencePlane.worldToLocalTo(center, this.polygonPreviewLocalCenter);
        const currentLocal = this.referencePlane.worldToLocalTo(currentPoint, this.polygonPreviewLocalCurrent);
        const referenceAngle = 0;
        const currentAngle = Math.atan2(currentLocal.y - centerLocal.y, currentLocal.x - centerLocal.x);
        const ccwDelta = THREE.MathUtils.euclideanModulo(currentAngle - referenceAngle, Math.PI * 2);
        const minorAngle = ccwDelta <= Math.PI
            ? ccwDelta
            : Math.PI * 2 - ccwDelta;
        const majorAngle = Math.PI * 2 - minorAngle;

        let arcStartAngle = referenceAngle;
        let arcSweepAngle = ccwDelta;
        if (ccwDelta <= Math.PI) {
            arcStartAngle = currentAngle;
            arcSweepAngle = majorAngle;
        }

        const arcPoints = this.updatePolygonPreviewArcPoints(
            center,
            previewArcRadius,
            arcStartAngle,
            arcSweepAngle,
        );
        if (!this.polygonPreviewArc || this.sketchDrawingManager.previewCircle !== this.polygonPreviewArc) {
            this.sketchDrawingManager.previewCircle = this.createPolygonPreviewArc(arcPoints);
            this.polygonPreviewArc = this.sketchDrawingManager.previewCircle;
            this.viewer.addObject(this.sketchDrawingManager.previewCircle);
        } else {
            this.updatePolygonDashedPreviewLine(this.polygonPreviewArc, arcPoints);
        }

        const fixedReferenceLinePoints = this.updatePolygonFixedReferenceLinePoints(center, horizontalRightPoint);
        const currentPointerLinePoints = this.updatePolygonPointerReferenceLinePoints(center, currentPoint);
        if (
            !this.polygonFixedReferenceLinePreview
            || !this.polygonPointerReferenceLinePreview
            || this.sketchDrawingManager.previewConstructionLines[0] !== this.polygonFixedReferenceLinePreview
            || this.sketchDrawingManager.previewConstructionLines[1] !== this.polygonPointerReferenceLinePreview
        ) {
            const fixedReferenceLine = this.createPolygonPreviewConstructionLine(
                fixedReferenceLinePoints,
                '多边形预览右侧参考线-屏幕空间虚线材质',
            );
            const currentPointerLine = this.createPolygonPreviewConstructionLine(
                currentPointerLinePoints,
                '多边形预览指针参考线-屏幕空间虚线材质',
            );
            this.polygonFixedReferenceLinePreview = fixedReferenceLine;
            this.polygonPointerReferenceLinePreview = currentPointerLine;
            this.sketchDrawingManager.previewConstructionLines = [fixedReferenceLine, currentPointerLine];
            this.sketchDrawingManager.previewConstructionLines.forEach((line) => {
                this.viewer.addObject(line);
            });
        } else {
            this.updatePolygonDashedPreviewLine(this.polygonFixedReferenceLinePreview, fixedReferenceLinePoints);
            this.updatePolygonDashedPreviewLine(this.polygonPointerReferenceLinePreview, currentPointerLinePoints);
        }

        const majorAngleDeg = (majorAngle * 180) / Math.PI;
        const previewLabelText = this.buildPolygonPreviewMeasurementLabel(referenceRadius, majorAngleDeg);
        this.sketchDrawingManager.showPreviewAngleLabel(currentPoint, previewLabelText);
    }


    /**
     * 构建多边形预览右上角标签文本。
     * 说明：
     * 1. 标签内容由“中心到当前鼠标点的长度”和“大弧角度”组成；
     * 2. 长度显示必须遵循草图环境“UI 毫米、底层米制”的统一规则；
     * 3. 右上角预览标签按交互反馈要求固定保留 2 位小数；
     * 4. 角度部分保留度数符号，长度部分沿用当前草图数值显示习惯，不额外追加单位。
     * @param lengthValue 中心到当前鼠标点的长度。
     * @param angleDeg 角度值（度）。
     * @returns 预览标签文本。
     */
    private buildPolygonPreviewMeasurementLabel(lengthValue: number, angleDeg: number): string {
        const displayLength = convertMToMm(lengthValue).toFixed(2);
        const displayAngle = angleDeg.toFixed(2);
        return `${displayLength},  ${displayAngle}°`;
    }


    /**
     * 解析给定多边形集合对应的辅助圆对象。
     * 说明：
     * 1. 仅允许从 `SketchCollectionObject.children` 直接解析；
     * 2. 找不到或数量异常直接抛错，不做兜底。
     * @param collectionObject 多边形集合对象。
     * @returns 对应辅助圆对象。
     */
    private resolvePolygonConstructionCircleObject(collectionObject: SketchCollectionObject): SketchCircleObject {
        const childConstructionCircles = collectionObject.children.filter((child) => (
            isSketchCircleObject(child) && child.isConstruction
        ));
        if (childConstructionCircles.length !== 1) {
            throw new Error(`多边形集合中的辅助圆数量非法，期望 1 个，实际 ${childConstructionCircles.length} 个。`);
        }
        return childConstructionCircles[0] as SketchCircleObject;
    }

    /**
     * 解析多边形集合当前有效的全部直线边。
     *
     * @param collectionObject 多边形集合对象。
     * @returns 保持集合源顺序的直线边数组。
     * @throws 当集合中混入非直线源时抛出明确错误。
     */
    private resolvePolygonLineEdges(collectionObject: SketchCollectionObject): SketchLineObject[] {
        const sources = [...collectionObject.collectSet];
        if (!sources.every((source) => isSketchLineObject(source))) {
            throw new Error('多边形集合包含非直线边，无法同步种子线与阵列关系。');
        }
        return sources as SketchLineObject[];
    }

    /**
     * 同步当前模式下种子线与构造圆关系及唯一阵列聚合关系。
     *
     * @param constructionCircle 多边形辅助构造圆。
     * @param previousEdges 参数重建前的旧外框边；首次绘制时为空。
     * @param currentEdges 当前有效外框边。
     * @param patternBuildResult 当前阵列定义、实例与待绑定 Patterned 关系。
     * @throws 关系替换或统一参数求解失败时抛出错误。
     */
    private syncPolygonRelations(
        constructionCircle: SketchCircleObject,
        previousEdges: readonly SketchLineObject[],
        currentEdges: readonly SketchLineObject[],
        patternBuildResult: SketchCircularLinePatternBuildResult,
    ): void {
        const synchronized = this.polygonRelationSyncPort.syncRelations({
            constructionCircle,
            previousEdges,
            currentEdges,
            patternRelationAttachment: patternBuildResult.relationAttachment,
            shouldCreateTangency: this.polygonOptions.mode === PolygonMode.Inscribed,
        });
        if (!synchronized) {
            throw new Error('多边形种子线与圆周阵列关系同步失败。');
        }
    }

    /**
     * 按参数同步关联辅助圆的圆心与半径。
     * @param collectionObject 多边形集合对象。
     * @param parameters 多边形参数。
     * @param circumRadius 外接圆半径。
     */
    private syncPolygonConstructionCircleByParameters(
        collectionObject: SketchCollectionObject,
        parameters: PolygonParameters,
        circumRadius: number,
    ): void {
        const constructionCircle = this.resolvePolygonConstructionCircleObject(collectionObject);
        const constructionCircleRadius = this.resolveConstructionCircleRadius(parameters.radius, circumRadius);
        constructionCircle.centerPoint = new THREE.Vector2(parameters.center2d[0], parameters.center2d[1]);
        constructionCircle.radius = constructionCircleRadius;
    }


    /**
     * 构建多边形辅助构造圆（草图圆对象）。
     * @param center 圆心世界坐标。
     * @param radius 半径。
     * @returns 构造圆草图对象；半径非法时返回 null。
     */
    public buildPolygonConstructionCircle(
        center: THREE.Vector3,
        radius: number,
    ): SketchCircleObject | null {
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return null;
        }
        const material = this.createSketchLineMaterial(true, false);
        return new SketchCircleObject({
            center: center.clone(),
            radius,
            material,
            referencePlane: this.referencePlane,
            isConstruction: true,
        });
    }

    /**
     * 在处理器内创建多边形预览。

     * @param currentPoint 当前预览点。
     */
    private createPolygonPreviewInHandler(currentPoint: THREE.Vector3): void {
        const center = this.drawingPoints[0];
        if (!center) {
            this.destroyPreview();
            this.polygonParameters = null;
            this.polygonParametersSubject.next(null);
            return;
        }

        const polygonData = this.buildRegularPolygonFromCenter(
            center,
            currentPoint,
            this.polygonOptions,
        );
        if (!polygonData) {
            this.destroyPreview();
            this.polygonParameters = null;
            this.polygonParametersSubject.next(null);
            return;
        }
        const isConstruction = this.polygonOptions.forConstruction;
        if (!this.isPolygonPreviewActive(isConstruction)) {
            this.destroyPreview();
        }
        const closedPoints = this.updatePolygonPreviewOutlinePoints(polygonData.points);
        this.updatePolygonPreviewOutlineLine(closedPoints, isConstruction);
        this.syncPolygonInternalArcPreview(
            center,
            currentPoint,
            polygonData.referenceRadius,
            polygonData.circumRadius,
        );
        this.polygonParameters = {
            center: center.toArray() as [number, number, number],
            center2d: [Number(polygonData.localCenter.x.toFixed(8)), Number(polygonData.localCenter.y.toFixed(8))],
            radius: Number(polygonData.referenceRadius.toFixed(8)),
            rotation: Number((polygonData.rotation * 180 / Math.PI).toFixed(8)),
            sides: Math.max(3, Math.floor(this.polygonOptions.sides || 3)),
            mode: this.polygonOptions.mode,
        };
        this.polygonParametersSubject.next(this.polygonParameters);
    }


    /**
     * 创建一条多边形外框线。
     *
     * @param startPoint 外框线起点。
     * @param endPoint 外框线终点。
     * @returns 使用当前构造几何选项的草图直线。
     */
    private createPolygonLineObject(
        startPoint: SketchPointObject,
        endPoint: SketchPointObject,
    ): SketchLineObject {
        const line = new SketchLineObject({
            startPoint,
            endPoint,
            material: this.createSketchLineMaterial(this.polygonOptions.forConstruction, false),
            referencePlane: this.referencePlane,
        });
        if (line.material instanceof THREE.LineDashedMaterial || isDashDotLIneNodeMaterial(line.material)) {
            line.computeLineDistances();
        }
        return line;
    }

    /**
     * 创建多边形内部圆周阵列的参数快照。
     *
     * 阵列实例数始终包含种子线，完整 360 度等间距且不允许跳过实例。
     *
     * @param constructionCircle 多边形辅助构造圆。
     * @param seedLine 多边形唯一种子线。
     * @param sides 多边形边数。
     * @returns 内部圆周阵列参数。
     */
    private createPolygonCircularPatternParameters(
        constructionCircle: SketchCircleObject,
        seedLine: SketchLineObject,
        sides: number,
    ): SketchCircularPatternParameters {
        const centerLocal = constructionCircle.centerPoint;
        const startLocal = this.referencePlane.worldToLocal(seedLine.startPoint.getPosition());
        const endLocal = this.referencePlane.worldToLocal(seedLine.endPoint.getPosition());
        const midpointX = (startLocal.x + endLocal.x) / 2;
        const midpointY = (startLocal.y + endLocal.y) / 2;
        return {
            center2d: [centerLocal.x, centerLocal.y],
            radius: Math.hypot(midpointX - centerLocal.x, midpointY - centerLocal.y),
            startAngleDeg: THREE.MathUtils.radToDeg(Math.atan2(
                midpointY - centerLocal.y,
                midpointX - centerLocal.x,
            )),
            totalAngleDeg: 360,
            equalSpacing: true,
            spacingAngleDeg: 360 / sides,
            reverseDirection: false,
            instanceCount: sides,
            rotateInstances: true,
            showRadiusDimension: false,
            showSpacingDimension: false,
            showInstanceCount: false,
            skippedInstanceIndexes: [],
        };
    }

    /**
     * 从唯一种子线创建多边形的圆周阵列与闭合集合。
     *
     * @param polygonPoints 正多边形顶点世界坐标；只使用前两点创建种子线。
     * @param constructionCircle 多边形辅助构造圆。
     * @returns 多边形集合与圆周阵列构建结果。
     */
    private createPolygonPatternGeometryObjectInHandler(
        polygonPoints: THREE.Vector3[],
        constructionCircle: SketchCircleObject,
    ): {
        readonly collectionObject: SketchCollectionObject;
        readonly patternBuildResult: SketchCircularLinePatternBuildResult;
    } {
        const seedStartPoint = this.createLineControlPoint(polygonPoints[0]!);
        const seedEndPoint = this.createLineControlPoint(polygonPoints[1]!);
        const seedLine = this.createPolygonLineObject(seedStartPoint, seedEndPoint);
        const patternBuildResult = this.circularLinePatternBuilder.build({
            centerPoint: constructionCircle.getCenterControlPoint(),
            seedLine,
            parameters: this.createPolygonCircularPatternParameters(
                constructionCircle,
                seedLine,
                polygonPoints.length,
            ),
            createPoint: (position) => this.createLineControlPoint(position),
            createLine: (startPoint, endPoint) => this.createPolygonLineObject(startPoint, endPoint),
        });
        const collectionObject = new SketchCollectionObject({
            sources: [...patternBuildResult.lines],
            referencePlane: this.referencePlane,
            creationSource: SketchCollectionCreationSource.Polygon,
        });
        collectionObject.geometryData = {
            circularPatternPersistentId: patternBuildResult.patternObject.persistentId,
        };

        const uniquePoints = new Map(
            patternBuildResult.lines.flatMap((line) => [line.startPoint, line.endPoint])
                .map((point) => [point.uuid, point] as const),
        );
        uniquePoints.forEach((point) => {
            if (point.parent !== collectionObject) {
                addObject3DToParent(point, collectionObject);
            }
        });
        patternBuildResult.lines.forEach((line) => {
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line);
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line.startPoint);
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line.endPoint);
        });
        return {collectionObject, patternBuildResult};
    }


    /**
     * 在处理器内创建多边形实体。
     */
    private createPolygonEntityByHandler(): void {
        if (this.drawingPoints.length < 2 ) {
            return;
        }

        // 每次创建前先清空旧引用，避免创建失败时误用历史对象。
        this.currentPolygonCollectionObject = null;

        const center = this.drawingPoints[0];
        const edgePoint = this.drawingPoints[1];
        const polygonData = this.buildRegularPolygonFromCenter(center, edgePoint, this.polygonOptions,);
        if (!polygonData) {
            return;
        }

        const {points, localCenter, referenceRadius, circumRadius, rotation} = polygonData;
        const constructionCircleRadius = this.resolveConstructionCircleRadius(referenceRadius, circumRadius);
        const constructionCircle = this.buildPolygonConstructionCircle(center, constructionCircleRadius);
        if (!constructionCircle) {
            throw new Error('多边形辅助构造圆创建失败。');
        }
        const {collectionObject: polygonSegmentGeometry, patternBuildResult}
            = this.createPolygonPatternGeometryObjectInHandler(points, constructionCircle);
        addObject3DToParent(constructionCircle, polygonSegmentGeometry);

        this.currentPolygonCollectionObject = polygonSegmentGeometry;


        this.viewer.addSketchObject(polygonSegmentGeometry);
        this.sketchDrawingManager.notifySketchEntityCreated(polygonSegmentGeometry);

        const sketchGroupManager = this.viewer.sketchGroupManager;
        if (!sketchGroupManager) {
            throw new Error('草图组管理器未初始化，无法同步草图轮廓显示状态。');
        }
        const activeSketchGroup = sketchGroupManager.activeSketchGroup;
        if (!activeSketchGroup) {
            throw new Error('当前没有活动草图组，无法挂载多边形圆周阵列定义。');
        }
        polygonSegmentGeometry.add(patternBuildResult.patternObject);

        // 构造圆、种子线和阵列实例入树后，一次性绑定全部关系并执行统一参数求解。
        this.syncPolygonRelations(
            constructionCircle,
            [],
            this.resolvePolygonLineEdges(polygonSegmentGeometry),
            patternBuildResult,
        );
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(constructionCircle);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(constructionCircle.getCenterControlPoint());
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(constructionCircle.getEdgeControlPoint());
        if (this.viewer.shadedSketchContoursEnabled) {
            constructionCircle.syncContour();
            polygonSegmentGeometry.syncContour();
        }
        this.sketchDrawingManager.notifySketchEntityCreated(constructionCircle);
        this.viewer.syncShadedContoursAfterSketchEntityCreated(activeSketchGroup);

        const round = (value: number): number => Number(value.toFixed(8));
        this.polygonParameters = {
            center: center.toArray() as [number, number, number],
            center2d: [round(localCenter.x), round(localCenter.y)],
            radius: round(referenceRadius),
            rotation: round((rotation * 180) / Math.PI),
            sides: Math.max(3, Math.floor(this.polygonOptions.sides || 3)),
            mode: this.polygonOptions.mode,
        };
        this.polygonParametersSubject.next(this.polygonParameters);
    }


    /**
     * 根据当前参数重建多边形集合对象。
     * 说明：
     * 1. 当传入 `targetCollectionObject` 时，重建该对象；
     * 2. 未传入时重建处理器缓存的当前对象；
     * 3. 重建后会同步更新 `polygonParameters` 并派发参数流。
     * @param parameters 多边形参数快照。
     * @param targetCollectionObject 目标多边形集合对象（可选）。
     * @throws 当不存在可重建对象、半径非法或无法计算有效外接半径时抛出错误。
     */
    public rebuildPolygonCollectionByParameters(
        parameters: PolygonParameters,
        targetCollectionObject?: SketchCollectionObject,
    ): void {
        const collectionObject = targetCollectionObject ?? this.currentPolygonCollectionObject;
        if (!collectionObject) {
            throw new Error('当前不存在可重建的多边形集合对象。');
        }

        const center = new THREE.Vector3(...parameters.center);
        const sides = Math.max(3, Math.floor(this.polygonOptions.sides || 3));
        const referenceRadius = parameters.radius;
        if (!Number.isFinite(referenceRadius) || referenceRadius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            throw new Error('多边形半径参数非法，无法重建多边形。');
        }

        const rotationRad = THREE.MathUtils.degToRad(parameters.rotation);
        const step = (Math.PI * 2) / sides;
        const circumRadius = this.polygonOptions.mode === PolygonMode.Inscribed
            ? referenceRadius / Math.cos(step / 2)
            : referenceRadius;
        if (!Number.isFinite(circumRadius) || circumRadius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            throw new Error('多边形外接半径计算失败，无法重建多边形。');
        }

        const centerLocalRaw = this.referencePlane.worldToLocal(center);
        const centerLocal = new THREE.Vector2(centerLocalRaw.x, centerLocalRaw.y);
        const polygonPoints = this.circularDistributionCalculator.calculate({
            instanceCount: sides,
            startAngleRad: rotationRad,
            totalAngleRad: Math.PI * 2,
            equalSpacing: true,
            direction: 'counterclockwise',
        }).map((item) => {
            const angle = item.angleRad;
            const localX = centerLocal.x + circumRadius * Math.cos(angle);
            const localY = centerLocal.y + circumRadius * Math.sin(angle);
            return this.referencePlane.localToWorld(new THREE.Vector2(localX, localY));
        });

        // 重建前保留旧边引用，关系服务需要从旧种子线和派生边完整清理旧定义。
        const previousEdges = this.resolvePolygonLineEdges(collectionObject);
        const seedLine = previousEdges[0];
        if (!seedLine) {
            throw new Error('多边形集合缺少种子线，无法重建圆周阵列。');
        }
        this.syncPolygonConstructionCircleByParameters(collectionObject, parameters, circumRadius);
        const constructionCircle = this.resolvePolygonConstructionCircleObject(collectionObject);
        const existingPattern = this.resolvePolygonCircularPatternObject(collectionObject);
        const patternBuildResult = this.rebuildPolygonCollectionObject(
            collectionObject,
            polygonPoints,
            constructionCircle,
            seedLine,
            existingPattern,
        );
        const currentEdges = [...patternBuildResult.lines];
        this.syncPolygonRelations(
            constructionCircle,
            previousEdges,
            currentEdges,
            patternBuildResult,
        );
        if (this.viewer.shadedSketchContoursEnabled) {
            collectionObject.syncContour();
        }
        this.currentPolygonCollectionObject = collectionObject;

        const round = (value: number): number => Number(value.toFixed(8));
        this.polygonParameters = {
            center: center.toArray() as [number, number, number],
            center2d: [round(centerLocal.x), round(centerLocal.y)],
            radius: round(referenceRadius),
            rotation: round(parameters.rotation),
            sides,
            mode: this.polygonOptions.mode,
        };
        this.polygonParametersSubject.next(this.polygonParameters);
    }

    /**
     * 根据当前参数重建“当前生成的多边形集合对象”。
     * @param parameters 多边形参数快照。
     */
    public rebuildCurrentPolygonCollectionByParameters(parameters: PolygonParameters): void {
        this.rebuildPolygonCollectionByParameters(parameters, this.currentPolygonCollectionObject ?? undefined);
    }

    /**
     * 解析多边形拥有的内部圆周阵列定义。
     *
     * @param collectionObject 目标集合对象。
     * @returns 与集合几何数据中持久 ID 一致的阵列定义。
     * @throws 集合缺少阵列归属或所属草图中缺少阵列对象时抛出错误。
     */
    private resolvePolygonCircularPatternObject(
        collectionObject: SketchCollectionObject,
    ): SketchCircularPatternObject {
        const patternPersistentId = collectionObject.geometryData?.circularPatternPersistentId;
        if (typeof patternPersistentId !== 'string' || patternPersistentId.length === 0) {
            throw new Error('多边形集合缺少内部圆周阵列持久 ID。');
        }
        const patternObject = collectionObject.children.find((child) => (
            child instanceof SketchCircularPatternObject
            && child.persistentId === patternPersistentId
        ));
        if (!(patternObject instanceof SketchCircularPatternObject)) {
            throw new Error(`多边形内部圆周阵列不存在：persistentId=${patternPersistentId}`);
        }
        return patternObject;
    }

    /**
     * 保留种子线并重建其余圆周阵列实例。
     *
     * @param collectionObject 目标多边形集合。
     * @param polygonPoints 当前正多边形顶点世界坐标。
     * @param constructionCircle 已同步圆心与半径的辅助构造圆。
     * @param seedLine 需要保留身份的唯一种子线。
     * @param patternObject 需要原位更新的圆周阵列定义。
     * @returns 新阵列实例、原位更新后的阵列定义与待绑定关系。
     */
    private rebuildPolygonCollectionObject(
        collectionObject: SketchCollectionObject,
        polygonPoints: THREE.Vector3[],
        constructionCircle: SketchCircleObject,
        seedLine: SketchLineObject,
        patternObject: SketchCircularPatternObject,
    ): SketchCircularLinePatternBuildResult {
        seedLine.startPoint.syncPositionSilently(polygonPoints[0]!);
        seedLine.endPoint.syncPositionSilently(polygonPoints[1]!);
        seedLine.refreshGeometryOnly();

        const patternBuildResult = this.circularLinePatternBuilder.build({
            centerPoint: constructionCircle.getCenterControlPoint(),
            seedLine,
            parameters: this.createPolygonCircularPatternParameters(
                constructionCircle,
                seedLine,
                polygonPoints.length,
            ),
            existingPattern: patternObject,
            existingLines: this.resolvePolygonLineEdges(collectionObject),
            createPoint: (position) => this.createLineControlPoint(position),
            createLine: (startPoint, endPoint) => this.createPolygonLineObject(startPoint, endPoint),
        });
        const activePointIds = new Set(
            patternBuildResult.lines.flatMap((line) => [line.startPoint.uuid, line.endPoint.uuid]),
        );
        collectionObject.children
            .filter((child) => isSketchPointObject(child) && !activePointIds.has(child.uuid))
            .forEach((point) => collectionObject.remove(point));
        collectionObject.setSources([...patternBuildResult.lines]);
        patternBuildResult.lines.flatMap((line) => [line.startPoint, line.endPoint]).forEach((point) => {
            if (point.parent !== collectionObject) {
                addObject3DToParent(point, collectionObject);
            }
        });
        collectionObject.geometryData = {
            ...(collectionObject.geometryData ?? {}),
            circularPatternPersistentId: patternBuildResult.patternObject.persistentId,
        };
        patternBuildResult.lines.forEach((line) => {
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line);
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line.startPoint);
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line.endPoint);
        });
        return patternBuildResult;
    }

}


